"use client";

import { FormEvent, startTransition, useActionState, useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
  clearExpiredSessionAction,
  confirmSessionTakeover,
  loginAction,
} from "@/app/actions";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { AlertCircle, Eye, EyeOff, ShieldAlert } from "lucide-react";
import Button from "@/app/_components/ui/Button";
import { Modal, ModalButton } from "@/app/_components/ui/Modal";
import { useUserStore } from "@/stores/userStore";
import { DEFAULT_LOCALE, getLocaleFromPath, resolveLocale } from "@/lib/language";
import { NAVIGATION_CONFIG } from "@/lib/navigation/config";
import { getFirstAllowedRouteByPermissions } from "@/lib/navigation/resolve";

type LoginState = {
  success: boolean;
  message?: string;
  errors?: {
    phoneNumber?: string[];
    password?: string[];
  };
  requiresTakeover?: boolean;
  takeoverToken?: string;
  sessionInfo?: {
    deviceInfo: string;
    ipAddress: string;
    lastActivity: string;
  };
  timestamp?: number;
};

const initialState: LoginState = { success: false, message: "" };

const ETHIOPIAN_PHONE_ERROR = "Enter a valid phone number (e.g. +2519XXXXXXXX or 09XXXXXXXX).";

function normalizeEthiopianPhone(input: string): string | null {
  const digits = input.replace(/\D/g, "");

  if (/^0[79]\d{8}$/.test(digits)) {
    return `+251${digits.slice(1)}`;
  }

  if (/^251[79]\d{8}$/.test(digits)) {
    return `+${digits}`;
  }

  if (/^[79]\d{8}$/.test(digits)) {
    return `+251${digits}`;
  }

  return null;
}

export default function LoginComponent() {
  const t = useTranslations("loginpage");
  const [state, formAction, pending] = useActionState(loginAction, initialState);

  const [showTakeoverModal, setShowTakeoverModal] = useState(false);
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [authFlowPending, setAuthFlowPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const handledSuccessRef = useRef(false);

  const router = useRouter();
  const pathname = usePathname();
  const fetchUserProfile = useUserStore((s) => s.fetchUserProfile);

  useEffect(() => {
    // If we landed on login due to an expired/invalid session, clear stale cookies.
    void clearExpiredSessionAction();
  }, []);

  useEffect(() => {
    const handleAuthFlow = async () => {
      if (state?.requiresTakeover) {
        handledSuccessRef.current = false;
        setAuthFlowPending(false);
        setSubmitError(null);
        setShowTakeoverModal(true);
        return;
      }

      if (!state?.success) {
        handledSuccessRef.current = false;
        setAuthFlowPending(false);
        setSubmitError(state?.message ?? null);
        return;
      }

      if (handledSuccessRef.current) {
        return;
      }

      handledSuccessRef.current = true;
      setAuthFlowPending(true);
      setSubmitError(null);

      try {
        const localeInPath = getLocaleFromPath(pathname) ?? DEFAULT_LOCALE;
        const profile = await fetchUserProfile({ force: true });
        const preferredLocale = resolveLocale(
          profile?.language?.code ?? profile?.languageCode ?? localeInPath
        );
        const allowedRoute = getFirstAllowedRouteByPermissions(
          NAVIGATION_CONFIG,
          profile?.permissions,
          preferredLocale
        );

        router.replace(allowedRoute ?? `/${preferredLocale}/dashboard-report`);
        router.refresh();
      } catch {
        handledSuccessRef.current = false;
        setAuthFlowPending(false);
        setSubmitError(t("redirectError"));
      }
    };

    handleAuthFlow();
  }, [state, pathname, fetchUserProfile, router, t]);

  const handleTakeover = async () => {
    if (!state.takeoverToken) return;

    setTakeoverLoading(true);

    // Call the takeover action
    const result = await confirmSessionTakeover(state.takeoverToken);

    setTakeoverLoading(false);

    if (result.success) {
      setShowTakeoverModal(false);
      const localeInPath = getLocaleFromPath(pathname) ?? DEFAULT_LOCALE;
      const profile = await fetchUserProfile({ force: true });
      const preferredLocale = resolveLocale(
        profile?.language?.code ?? profile?.languageCode ?? localeInPath
      );
      const allowedRoute = getFirstAllowedRouteByPermissions(
        NAVIGATION_CONFIG,
        profile?.permissions,
        preferredLocale
      );

      router.replace(allowedRoute ?? `/${preferredLocale}/dashboard-report`);
      router.refresh();
    } else {
      alert(result.message || t("takeoverFailed"));
    }
  };

  const handleLoginSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitError(null);
    const formData = new FormData(e.currentTarget);
    const submittedPhoneNumber = String(formData.get("phoneNumber") ?? "");

    // Keep controlled input in sync with the submitted DOM value
    // (covers autofill and fast-type+submit timing).
    if (submittedPhoneNumber !== phoneNumber) {
      setPhoneNumber(submittedPhoneNumber);
    }

    setPhoneTouched(true);
    const normalizedPhone = normalizeEthiopianPhone(submittedPhoneNumber);
    if (!normalizedPhone) {
      setPhoneError(t("phoneNumberPatternError"));
      return;
    }

    setPhoneError(null);

    formData.set("phoneNumber", normalizedPhone);
    startTransition(() => {
      formAction(formData);
    });
  };

  const isPhoneValid = !!normalizeEthiopianPhone(phoneNumber);
  const showPhoneError =
    (phoneTouched && phoneNumber.trim().length > 0 && !isPhoneValid) || !!phoneError;
  const showPhoneSuccess = phoneTouched && phoneNumber.trim().length > 0 && isPhoneValid;
  const isSubmitting = pending || authFlowPending;
  const loginErrorMessage =
    submitError ?? (state?.message && !state.success && !state.requiresTakeover ? state.message : null);

  return (
    <div id="logincontainer" className="min-h-screen flex items-center justify-center p-4">
      <div className="relative w-full max-w-lg bg-secondary border-4 border-gray-200 rounded-xl shadow-2xl px-2 lg:px-10 py-16">

        <div className="absolute -top-12 left-1/2 -translate-x-1/2">
          <div className="bg-white rounded-full p-1 border border-primary shadow-sm">
            <Image
              src="/images/logo.png"
              alt="Logo"
              width={100}
              height={100}
              className="rounded-full"
            />
          </div>
        </div>

        <div className="text-center mb-10">
          <h1 className="text-4xl font-black text-black tracking-tight">
            {t("title")}
          </h1>
        </div>

        {loginErrorMessage && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm mb-4 border border-red-100">
            {loginErrorMessage}
          </div>
        )}

        <form className="space-y-6" onSubmit={handleLoginSubmit}>
          <div className="space-y-1">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-500 ml-1">
              {t("phoneNumber")}
            </label>
            <input
              id="phone"
              name="phoneNumber"
              type="text"
              required
              placeholder="+251..."
              inputMode="tel"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                if (!phoneTouched) setPhoneTouched(true);
                if (phoneError) setPhoneError(null);
              }}
              onBlur={() => setPhoneTouched(true)}
              aria-invalid={showPhoneError}
              className={`w-full px-5 py-4 bg-white border rounded-lg text-xl focus:outline-none focus:ring-2 transition-all ${showPhoneError
                ? "border-red-400 focus:ring-red-200"
                : showPhoneSuccess
                  ? "border-green-500 focus:ring-green-200"
                  : "border-primary focus:ring-sky-500"
                }`}
            />
            {showPhoneError && (
              <p className="text-sm text-red-600 ml-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {phoneError ?? t("phoneNumberPatternError")}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="block text-sm font-medium text-gray-500 ml-1">
              {t("password")}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                className="w-full px-5 py-4 bg-white border border-primary rounded-lg text-xl focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary"
              >
                {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end px-1">
            <Link
              href="/auth/request-otp"
              className="font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {t("forget")}
            </Link>
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-primary hover:bg-primary/80 text-white font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 flex justify-center items-center"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  {t("loging")}...
                </span>
              ) : t("login")}
            </Button>
          </div>
        </form>
      </div>

      <Modal
        isOpen={showTakeoverModal}
        onClose={() => setShowTakeoverModal(false)}
        variant="simple"
        title={t("takeoverTitle")}
        description={t("takeoverDescription", {
          device: state?.sessionInfo?.deviceInfo || t("anotherDevice"),
        })}
        icon={
          <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-2">
            <ShieldAlert size={32} className="text-orange-500" />
          </div>
        }
        footer={
          <div className="flex flex-col gap-3 w-full">
            <ModalButton
              variant="primary"
              fullWidth
              onClick={handleTakeover}
              disabled={takeoverLoading}
            >
              {takeoverLoading ? t("takeoverProcessing") : t("acceptTakeover")}
            </ModalButton>

            <ModalButton
              variant="outline"
              fullWidth
              onClick={() => setShowTakeoverModal(false)}
              disabled={takeoverLoading}
            >
              {t("continueOldDevice")}
            </ModalButton>
          </div>
        }
      />
    </div>
  );
}
